import { useEffect } from 'react'
import {
  BoundedContent, Card, StandardTileImage, Column, useScreenClass, Carousel,
  GridContainer, Row, Typography, FluidImage, Box, Link, CarouselSlide,
  useIsScreenClassInitialized
} from '@vp/swan'
import { useLogger, useSwanStyleKeys, useUserContext, useUserContextTracker } from '@vp/ubik-context'
import { useTranslation } from 'react-i18next'
import { PlpData } from '../types'
import { LoadingClusterTiles } from './Loader'
import { getI18nLocale } from '../i18n/config'
import { createOnClickTrackingHandler, TrackingLinkConfig } from '../utils/trackingUtils'

interface TileGridProps {
  data?: PlpData;
  title: string;
  position: number;
}

export interface Props {
  newArrivals?: PlpData
  bestsellers?: PlpData
  saleDeals?: PlpData
  clothing?: PlpData
  everydayValue?: PlpData
  marketing?: PlpData
  fastDelivery?: PlpData
  shopAll?: PlpData
}

export const ClusterTiles = (props: Props) => {
  const screenClass = useScreenClass()
  const logger = useLogger()
  const userContext = useUserContext()
  const tracker = useUserContextTracker()
  const { t, i18n } = useTranslation()

  useEffect(() => {
    if (userContext.locale) {
      i18n.changeLanguage(getI18nLocale(userContext.locale))
    }
  }, [userContext.locale, i18n])

  const requiredSectionsByLocale: Record<string, string[]> = {
    'en-us': ['bestsellers', 'clothing', 'newArrivals'],
    'en-au': ['bestsellers', 'shopAll', 'everydayValue'],
    'fr-fr': ['bestsellers', 'marketing', 'fastDelivery']
  }

  const requiredSections = requiredSectionsByLocale[userContext.locale?.toLowerCase()] || ['bestsellers']
  const hasMissingData = requiredSections.some(section => !props[section as keyof Props]?.images?.length)

  if (hasMissingData) {
    return null
  }
  if (userContext.experiments?.homepage_cluster_tile_component) {
    tracker({
      type: 'experiment',
      name: 'homepage_cluster_tile_component',
      variation: userContext.experiments.homepage_cluster_tile_component,
    })
  }

  if (!userContext.experiments?.homepage_cluster_tile_component || userContext.experiments.homepage_cluster_tile_component === 'control') {
    return null
  }

  logger.log('Rendering fragment for a locale', userContext.locale)

  useSwanStyleKeys(['core', 'grid', 'standardTile', 'carousel', 'baseTile', 'visible', 'utility'])

  const getTrackingConfig = (plpLink: string, plpName: string, tilePosition: number): TrackingLinkConfig => {
    const pageZone = 'Cluster Tile Section'
    const sourcePagePath = typeof window !== 'undefined' ? window.location.pathname : '/'
    return {
      eventName: 'Navigation Clicked',
      linkAttributes: {
        'data-section': `Content:${pageZone}`,
        'data-position': tilePosition ?? 0,
        'data-translation': plpName,
      },
      properties: {
        category: 'Home Page',
        label: `Content:${pageZone}`,
        eventDetail: `${sourcePagePath};${plpLink};Cluster Tile Section;${tilePosition}`,
        navigationDetail: `${plpName};${tilePosition}`,
      },
    }
  }

  const TileGrid = ({ data, title, position }: TileGridProps) => {
    if (!data?.images?.length) {
      return null
    }
    const { url } = data
    return (
      <Card pl={5} pt={5} pr={2} pb={5} evenHeight={screenClass !== 'xs' && screenClass !== 'sm'} style={{ display: 'flex', flexDirection: 'column' }}>
        <Link
          href={url}
          onClick={createOnClickTrackingHandler(getTrackingConfig(url, title, position), url)}
          pl={0} pb={4} skin='cta' display='flex' style={{ alignItems: 'center' }}
        >
          {title}
        </Link>
        <GridContainer gutter='tight' style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <Row>
            {data.images.slice(0, 3).map((image) => (
              <Column pr={4} pl={0} key={image.mpvId} spanXs={4} span={4}>
                <StandardTileImage>
                  <FluidImage src={image.defaultImage} alt={image.mpvId} />
                </StandardTileImage>
              </Column>
            ))}
          </Row>
        </GridContainer>
      </Card>
    )
  }

  const availableSections = Object.entries(props).filter(([_, value]) => value?.images?.length > 0)

  const mobileCarouselGrid = () => {
    if (!availableSections.length) return null

    return (
      <Carousel
        slidesToShow={1.1}
        initialSlide={0}
        gridGutters
        data-testid='swan-carousel-container'
        infinite={false}
        progressIndicator='dots'
        arrows={false}
        aria-label={t('clusterTiles.accessibility.carouselLabel')}
        accessibleTextForDots={availableSections.map(([key]) => t(`clusterTiles.${key}`))}
        accessibleTextForVisibleSlides={t('clusterTiles.accessibility.visibleSlides')}
        aria-roledescription={t('clusterTiles.accessibility.carouselRoleDescription')}
      >
        {availableSections.map(([key, data], index) => (
          <CarouselSlide
            key={key}
            aria-roledescription={t('clusterTiles.accessibility.slideRoleDescription')}
          >
            <TileGrid
              title={data?.content?.title ?? t(`clusterTiles.${key}`)}
              data={data}
              position={index + 1}
            />
          </CarouselSlide>
        ))}
      </Carousel>
    )
  }

  return (
    <Box mb={9}>
      <BoundedContent bgc='info'>
        <GridContainer pb={{ xs: 7, md: 0 }}>
          <Typography fontSize='x2large' fontWeight='bold' mb={6} pt={7} pl={4}>
            {t('clusterTiles.title')}
          </Typography>
          {!useIsScreenClassInitialized() && <>{LoadingClusterTiles()}</>}
          {screenClass === 'xs' || screenClass === 'sm'
            ? (
              <>
                {useIsScreenClassInitialized() && mobileCarouselGrid()}
              </>
              )
            : (
              <Row>
                {availableSections.map(([key, data], index) => (
                  <Column key={key} span={4}>
                    <TileGrid
                      title={data?.content?.title ?? t(`clusterTiles.${key}`)}
                      data={data}
                      position={index + 1}
                    />
                  </Column>
                ))}
              </Row>
              )}
        </GridContainer>
      </BoundedContent>
    </Box>
  )
}
